import React from 'react';
import classNames from 'classnames';
import { PrimaryButton as Button } from 'jl-design-system/elements/button/Button';
import { CloseOutlined } from 'jl-design-system/components/icons/CloseOutlined';
import { SearchOutlined } from 'jl-design-system/components/icons/SearchOutlined';
import styles from './search-input.scss';

export interface SearchInputRefs {
  box: React.RefObject<HTMLInputElement>;
  form: React.RefObject<HTMLFormElement>;
  container: React.RefObject<HTMLDivElement>;
  results: React.RefObject<HTMLDivElement>;
}

interface SearchInputProps {
  id: string;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  loaded: boolean | null;
  loadSearchBoxFiles: (callback: () => void) => void;
  setLoaded: (loaded: boolean) => void;
  features: Features;
  showClear: boolean;
  parentOnFocus: () => void;
  isTypeaheadOpen: boolean;
  inputRefs: SearchInputRefs;
}

export const SearchInput = ({
  id,
  searchTerm,
  setSearchTerm,
  loaded,
  loadSearchBoxFiles,
  setLoaded,
  features,
  showClear,
  parentOnFocus,
  isTypeaheadOpen,
  inputRefs,
}: SearchInputProps) => {
  const placeholderDefaultText = 'Search product or brand';
  const placeholderText = isTypeaheadOpen ? '' : placeholderDefaultText;
  const { box, form, container, results } = inputRefs;

  const searchClassNames = classNames(styles.searchInput, {
    [styles.searchInput__active]: isTypeaheadOpen || showClear,
  });

  return (
    <div
      className={styles.searchInputContainer}
      data-testid="search-input-container"
    >
      <div
        className={classNames(styles.searchContainer, {
          [styles.searchContainer__active]: isTypeaheadOpen,
        })}
        data-testid="search-container"
      >
        <div className={styles.glassIconContainer}>
          <Button type="submit" className={styles.searchBarButton}>
            <SearchOutlined className={styles.invertedIcon} size="large" />
            <span className={styles.visuallyHidden}>Search</span>
          </Button>
        </div>
        <input
          type="text"
          name="search-term"
          id={id}
          placeholder={placeholderText}
          maxLength={150}
          autoComplete="off"
          value={searchTerm}
          ref={box}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setSearchTerm(e.target.value);
          }}
          className={searchClassNames}
          onFocus={() => {
            parentOnFocus();
            if (!loaded) {
              loadSearchBoxFiles(() => {
                if (typeof window !== 'undefined' && window.SearchBoxUI) {
                  window.SearchBoxUI.init(
                    results?.current ?? null,
                    container?.current ?? null,
                    form?.current ?? null,
                    box?.current ?? null,
                    features.monetateExperiences,
                    true,
                    {}
                  );
                }
              });
            }
            setLoaded(true);
          }}
        />
        <div className={styles.closeIconContainer}>
          <Button
            type="reset"
            className={classNames(styles.searchBarButton, {
              [styles.hide]: !showClear,
            })}
          >
            <CloseOutlined className={styles.invertedIcon} size="large" />
            <span className={styles.visuallyHidden}>Clear</span>
          </Button>
        </div>
      </div>
    </div>
  );
};
